import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  createBillingContract,
  listBillingContracts,
  updateBillingContract,
  type BillingContract,
  type BillingContractTerms,
} from '../api/onboardingContracts'

export function billingContractsKey(teamId: string): readonly string[] {
  return ['admin-onboarding', 'billing-contracts', teamId]
}

export function useBillingContracts(teamId: string | null) {
  return useQuery<BillingContract[]>({
    queryKey: billingContractsKey(teamId ?? ''),
    queryFn: () => listBillingContracts(teamId ?? ''),
    enabled: teamId !== null,
  })
}

function useInvalidateContracts() {
  const queryClient = useQueryClient()
  return (teamId: string) => {
    void queryClient.invalidateQueries({ queryKey: billingContractsKey(teamId) })
    void queryClient.invalidateQueries({ queryKey: ['onboarding-team-lookup'] })
    void queryClient.invalidateQueries({ queryKey: ['teams'] })
  }
}

export function useCreateBillingContract() {
  const invalidate = useInvalidateContracts()
  return useMutation<BillingContract, Error, { teamId: string; terms: BillingContractTerms }>({
    mutationFn: ({ teamId, terms }) => createBillingContract(teamId, terms),
    onSuccess: (_contract, variables) => invalidate(variables.teamId),
  })
}

export function useUpdateBillingContract() {
  const invalidate = useInvalidateContracts()
  return useMutation<BillingContract, Error, { teamId: string; contractId: string; terms: BillingContractTerms }>({
    mutationFn: ({ teamId, contractId, terms }) => updateBillingContract(teamId, contractId, terms),
    onSuccess: (_contract, variables) => invalidate(variables.teamId),
  })
}
